import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Infinity as InfinityIcon, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export type CapKey =
  | 'aiCompany'
  | 'candidates'
  | 'transcripts'
  // Sprint 2 (S2.D) — structural caps with their own dashboard widgets.
  | 'positions'
  | 'seats';

/** SME tier upgrade ladder. */
/**
 * Pricing v3.2 (21/05/26) — next-tier suggestion for cap warning widgets.
 * Maps both legacy aliases (starter/growth/professional) and v3.2 IDs
 * (sme_*) to the next paid tier. Returns null when at the top SME tier
 * (next step from there is conversion to Agency via the chooser flow).
 */
export function nextTierForCap(currentPlan: string): string | null {
  if (currentPlan === 'freemium_trial') return 'sme_starter';
  if (currentPlan === 'sme_starter' || currentPlan === 'starter') return 'sme_growth';
  if (currentPlan === 'sme_growth' || currentPlan === 'growth') return 'sme_professional';
  return null; // already on top SME tier — conversion to Agency suggested via chooser
}

interface Props {
  icon: LucideIcon;
  label: string;
  granted: number;
  remaining: number;
  unlimited?: boolean;
  planId: string;
  capKey: CapKey;
  /** When the cap is 0 because the plan does not include it (e.g. trial AI). */
  notIncluded?: boolean;
  notIncludedLabel?: string;
}

export function CapUsageWidget({
  icon: Icon, label, granted, remaining,
  unlimited, planId, capKey, notIncluded, notIncludedLabel,
}: Props) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const nextTier = nextTierForCap(planId);
  const goUpgrade = () => {
    if (!nextTier) return;
    // v3.5.2 polish (26/05/26): Subscription tab vive in /profile?tab=billing,
    // non più /settings?tab=subscription (legacy redirect senza query param
    // preservation).
    navigate(`/profile?tab=billing&plan=${nextTier}`);
  };

  // ── Edge: not included on this plan (trial AI etc.)
  if (notIncluded) {
    return (
      <Card
        role="region"
        aria-label={label}
        className="p-4 flex flex-col gap-2 border-border/60"
      >
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span className="truncate">{label}</span>
        </div>
        <p className="text-sm font-medium text-foreground">
          {notIncludedLabel ?? t('usage.trial_no_ai')}
        </p>
        {nextTier && (
          <Button size="sm" variant="default" className="mt-auto" onClick={goUpgrade}>
            {nextTier === 'sme_starter' || nextTier === 'starter'
              ? t('usage.cta_upgrade_to_starter')
              : t('usage.cta_upgrade')}
          </Button>
        )}
      </Card>
    );
  }

  // ── Edge: unlimited
  if (unlimited) {
    return (
      <Card
        role="region"
        aria-label={label}
        className="p-4 flex flex-col gap-2 border-border/60"
      >
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span className="truncate">{label}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <InfinityIcon className="h-6 w-6 text-primary" />
          <span className="text-xs text-muted-foreground">
            {t('usage.unlimited')}
          </span>
        </div>
      </Card>
    );
  }

  // ── Standard: progress bar
  const used = Math.max(0, granted - remaining);
  const pct = granted > 0 ? Math.min(100, Math.max(0, Math.round((used / granted) * 100))) : 0;

  let barColor = 'bg-green-500';
  let textTone = 'text-foreground';
  let footer: React.ReactNode = null;

  if (pct >= 100) {
    barColor = 'bg-red-600';
    textTone = 'text-red-700';
    footer = (
      <div
        className="text-[11px] text-red-700"
        title="Le prossime azioni saranno bloccate"
      >
        {t('usage.cap_reached')}
        {nextTier && (
          <button
            onClick={goUpgrade}
            className="font-semibold underline hover:no-underline"
          >
            {t('usage.cta_upgrade')} →
          </button>
        )}
      </div>
    );
  } else if (pct >= 80) {
    barColor = 'bg-orange-500';
    textTone = 'text-orange-700';
    footer = (
      <div className="text-[11px] text-orange-700">
        {t('usage.approaching_cap')}
        {nextTier && (
          <button
            onClick={goUpgrade}
            className="font-semibold underline hover:no-underline"
          >
            {t('usage.cta_upgrade')} →
          </button>
        )}
      </div>
    );
  } else if (pct >= 51) {
    barColor = 'bg-yellow-500';
  }

  return (
    <Card
      role="region"
      aria-label={label}
      className="p-4 flex flex-col gap-2 border-border/60"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={cn('text-xl font-semibold', textTone)}>{used}</span>
        <span className="text-xs text-muted-foreground">
          {t('usage.of')} {granted}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 w-full bg-secondary rounded-full overflow-hidden"
      >
        <div
          className={cn('h-full transition-all', barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{pct}%</span>
      </div>
      {footer}
    </Card>
  );
}

export default CapUsageWidget;